import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { ApiService } from './api.service';
import { EMPTY, Observable } from 'rxjs';
import { map, catchError, expand, reduce } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AlarmsQuery, ListQuery, NotificationsQuery, TraitsQuery, readPage, toApiInstant, toQueryParams } from '../alarms-api';
import { PatientsQuery } from '../patients-api';
import { withNormalizedBandLanguage, withNormalizedBandHours } from '../models/band-response.model';

import moment from 'moment';

function fromLocalWallClock(value: string): Date {
  return new Date(value.replace(' ', 'T'));
}

function toUtcWallClock(localWallClock: string): string {
  return fromLocalWallClock(localWallClock).toISOString().slice(0, 19).replace('T', ' ');
}

function instantWindowQuery(fromDate: string, toDate: string): string {
  return `fromDate=${toApiInstant(fromLocalWallClock(fromDate))}&toDate=${toApiInstant(fromLocalWallClock(toDate))}`;
}

@Injectable()
export class PatientService {

  constructor(
    private apiService: ApiService,
    private http: HttpClient
  ) {
  }

  getPatients(): Observable<any> {
    return this.apiService.get('/patient/my');
  }

  getPatientsShortInfo(page, count): Observable<any> {
    return this.apiService.get(`/patient/myShortInfo?page=${page}&count=${count}`);
  }

  findCaregiverPatients(caregiverId, query: PatientsQuery): Observable<any> {
    return this.apiService.get(`/caregivers/${caregiverId}/patients`, toQueryParams(query));
  }

  getPatient(imei): Observable<any> {
    return this.apiService.get(`/patient/${imei}`);
  }

  getPatientIntegrate(imei): Observable<any> {
    return this.apiService.get(`/patientIntegrate/${imei}`);
  }

  // Dane opaski prostujemy tutaj, na granicy z API, zamiast w każdym ekranie z osobna:
  // kod języka bywa zapisany starym kodem kraju (CZ), a godziny ciszy nocnej przychodzą
  // w dwóch zapisach. Ekrany dopasowują wartości do pól dokładnie, więc muszą dostać
  // jedną, przewidywalną postać.
  getPatientToEdit(imei): Observable<any> {
    return this.apiService.get(`/manager/patient-edit-initial-data/${imei}`)
      .pipe(map(withNormalizedBandLanguage), map(withNormalizedBandHours));
  }

  getPatientCardToEdit(imei): Observable<any> {
    return this.apiService.get(`/patientCard/${imei}`);
  }

  addPatient(data): Observable<any> {
    return this.apiService.post('/manager/patient', data);
  }

  editPatient(imei, data): Observable<any> {
    return this.apiService.patch(`/manager/patient/${imei}`, data);
  }

  /**
   * Restrykcje jednego pacjenta. Lista ZASTĘPUJE poprzednią, więc buduj ją z aktualnej
   * (`withAlarmPhonesEdit` w models/patient-restrictions.model). Zwraca stan po zapisie.
   */
  setPatientRestrictions(identifier, restrictions: string[]): Observable<any> {
    return this.apiService.post(`/manager/patient/${identifier}/restrictions`, { restrictions });
  }

  /**
   * Jedna restrykcja naraz dla wszystkich podopiecznych w zasięgu konta — celowo nie lista,
   * żeby zbiorcze kliknięcie nie kasowało blokad nałożonych wcześniej pojedynczo.
   * Zwraca `patients: { total, updated }`.
   */
  setPatientsRestriction(restriction: string, restricted: boolean): Observable<any> {
    return this.apiService.post('/manager/patients/restrictions', { restriction, restricted });
  }

  /** SDLY-3099: filtr niskich saturacji — `enabled` (manager ma przywilej) + `optedOut` (IMEI wyłączonych). */
  getSatFilter(): Observable<any> {
    return this.apiService.get('/manager/sat-confirm');
  }

  /** Wyłączenie/włączenie filtra podopiecznym (`imeis`) albo wszystkim (`all`). Zwraca `updated`. */
  setSatFilter(enabled: boolean, imeis: string[] | null): Observable<any> {
    return this.apiService.put('/manager/sat-confirm/patients', imeis ? { enabled, imeis } : { enabled, all: true });
  }

  editPatientCard(imei, data): Observable<any> {
    return this.apiService.patch(`/patientCard/create/${imei}`, data);
  }

  deletePatient(id): Observable<any> {
    return this.apiService.delete(`/manager/patient/${id}`);
  }

  getPatientsToAssign(caregiver): Observable<any> {
    return this.apiService.get(`/manager/available-patient-to-assign/${caregiver}`);
  }

  getPatientsToDetach(caregiver): Observable<any> {
    return this.apiService.get(`/manager/available-patient-to-detach/${caregiver}`);
  }

  getAlarm(imei, type): Observable<any> {
    return this.apiService.get(`/patient/${imei}/alarm/${type}`);
  }

  getPatientIntegrateAlarm(imei): Observable<any> {
    return this.apiService.get(`/patientIntegrateAlarm/${imei}`);
  }

  getAlarmEBS(imei, type): Observable<any> {
    return this.apiService.get(`/patient/${imei}/alarmEBS/${type}`);
  }

  getDiagramStats(imei, fromDate, toDate): Observable<any> {
    return this.apiService.get(`/patient/${imei}/stats?fromDate=${toUtcWallClock(fromDate)}&toDate=${toUtcWallClock(toDate)}`);
  }

  getMedicines(imei): Observable<any> {
    return this.apiService.get(`/manager/patient/${imei}/drugs`);
  }

  getMedicinesSchedule(imei): Observable<any> {
    return this.apiService.get(`/manager/patient/${imei}/medicationSchedules`);
  }

  getMedicine(id): Observable<any> {
    return this.apiService.get(`/manager/patient/drugs/${id}`);
  }

  getMedicineSchedule(id): Observable<any> {
    return this.apiService.get(`/manager/patient/medicationSchedules/${id}`);
  }

  addMedicine(data): Observable<any> {
    return this.apiService.post('/manager/patient/drug', data);
  }

  addMedicationSchedule(data): Observable<any> {
    return this.apiService.post('/manager/patient/medicationSchedule', data);
  }

  editMedicine(id, data): Observable<any> {
    return this.apiService.post(`/manager/patient/drug/${id}`, data);
  }

  editMedicineSchedule(id, data): Observable<any> {
    return this.apiService.post(`/manager/patient/medicationSchedule/${id}`, data);
  }

  deleteMedicine(id): Observable<any> {
    return this.apiService.delete(`/manager/patient/drug/${id}`);
  }

  deleteMedicineSchedule(id): Observable<any> {
    return this.apiService.delete(`/manager/patient/medicationSchedule/${id}`);
  }

  generateRaport(imei, data): Observable<any> {
    return this.apiService.post(`/patient/report/${imei}`, data);
  }

  generateRaportTMP(data): Observable<any> {
    return this.apiService.post(`/patient/reportTMP/`, data);
  }


  handleNotification(notifId: number): Observable<any> {
    return this.apiService.get(`/notification-board2/${notifId}`);
  }

  /** Kasuje wiersz nieodwracalnie. Cudze i nieistniejące powiadomienie dają tak samo 404. */
  deleteNotification(notifId: number): Observable<any> {
    return this.apiService.delete(`/notifications/${notifId}`);
  }

  getAlarmDetails(alarmId: number): Observable<any> {
    return this.apiService.get(`/alarm/details/${alarmId}`);
  }

  // --- Nowe API list: paginacja i liczniki po stronie serwera -------------------
  // Odpowiedź zawsze w kopercie { status, data: { items, pagination } }.
  // Filtry idą przez toQueryParams, bo backend odpowiada 400 na nieznaną wartość
  // filtra — a pusty string jest właśnie taką wartością.

  /**
   * Lista podopiecznych stronicowana po stronie serwera. Osobna trasa obok
   * `/patient/myShortInfo`, która zostaje nietknięta — ma jeszcze dwóch konsumentów
   * w panelu (caregiver-details-c, caretaker-schedule-users) i oddaje gołą tablicę,
   * bez `pagination`, więc nie da się z niej zbudować paginacji z numerami stron.
   *
   * `/ranked`, a nie `/patients`: tamta trasa trzyma alarmy na górze bezwarunkowo,
   * a `dir` nie sięga jej priorytetów — nie umie wyrazić łańcucha z panelu.
   */
  findPatients(query: PatientsQuery = {}): Observable<any> {
    return this.apiService.get('/patients/ranked', toQueryParams(query));
  }

  findAllPatients(): Observable<any[]> {
    const pageAt = (offset: number) => this.findPatients({ sort: 'name', limit: 200, offset: offset })
      .pipe(map(response => readPage(response)));

    return pageAt(0).pipe(
      expand(page => page.hasMore ? pageAt(page.offset + page.limit) : EMPTY),
      reduce((patients: any[], page) => patients.concat(page.items), [])
    );
  }

  /** Ranga kryteriów porządkowania listy. Statyczna — nie dotyka danych i nie bierze filtrów. */
  findPatientsOrder(): Observable<any> {
    return this.apiService.get('/patients/ranked/order');
  }

  findAlarms(query: AlarmsQuery = {}): Observable<any> {
    return this.apiService.get('/alarms', toQueryParams(query));
  }

  /** Przypięte alarmy zespołu (telecentrum): [{alarmId, pinnedBy, pinnedByName, pinnedAt}] */
  getPinnedAlarms(): Observable<any> {
    return this.apiService.get('/alarms/pinned');
  }

  pinAlarm(alarmId: number): Observable<any> {
    return this.apiService.post(`/alarms/${alarmId}/pin`);
  }

  unpinAlarm(alarmId: number): Observable<any> {
    return this.apiService.delete(`/alarms/${alarmId}/pin`);
  }

  findNotifications(query: NotificationsQuery = {}): Observable<any> {
    return this.apiService.get('/notifications', toQueryParams(query));
  }

  findTraits(query: TraitsQuery = {}): Observable<any> {
    return this.apiService.get('/traits', toQueryParams(query));
  }

  /** Wszystkie wykrycia opaski (także niepotwierdzone) z powodem decyzji — tylko telecentrum. */
  explainTraits(imei: string): Observable<any> {
    return this.apiService.get('/traits/explain', toQueryParams({ imei }));
  }

  /** Powody dla konkretnych wpisów (strona listy na Pulpicie) — tylko telecentrum. */
  explainTraitIds(ids: number[]): Observable<any> {
    return this.apiService.get('/traits/explain', toQueryParams({ ids: ids.join(',') }));
  }

  /** Liczniki dla wszystkich trzech zakładek naraz: sekcje alarms, notifications, traits. */
  getDashboardStats(query: ListQuery = {}): Observable<any> {
    return this.apiService.get('/alarms/stats', toQueryParams(query));
  }

  getAlarms(imei): Observable<any> {
    return this.apiService.get(`/patient/${imei}/all`);
  }

  findCaregiverAlarms(caregiverId, query: AlarmsQuery): Observable<any> {
    return this.apiService.get(`/caregivers/${caregiverId}/alarms`, toQueryParams(query));
  }

  formContact(mail, phone, reason, type): Observable<any> {
    return this.apiService.get(`/contact/${mail}/${phone}/${reason}/${type}`);
  }

  questionnaireContact(expection, howoften, isok, more): Observable<any> {
    return this.apiService.get(`/questionnaire/${expection}/${howoften}/${isok}/${more}`);
  }

  getUserContact(): Observable<any> {
    return this.apiService.get(`/phoneandmail/`);
  }

  //BEACONS

  getBeacons(): Observable<any> {
    return this.apiService.get(`/get-beacons`);
  }

  getBeaconFromMac(mac): Observable<any> {
    return this.apiService.get(`/getBeacon/mac/${mac}`);
  }

  getBeacon(id): Observable<any> {
    return this.apiService.get(`/get-beacon/${id}`);
  }

  editBeacon(id, mac, name, address, lat, lon): Observable<any> {
    return this.apiService.get(`/edit-beacon/${id}/${mac}/${name}/${address}/${lat}/${lon}`);
  }

  deleteBeacon(id): Observable<any> {
    return this.apiService.delete(`/delete-beacon/${id}`);
  }

  addBeacon(data): Observable<any> {
    return this.apiService.post('/beacon-create', data);
  }

  addBeacon2(mac, name, address, lat, lon): Observable<any> {
    return this.apiService.get(`/create-beacon/${mac}/${name}/${address}/${lat}/${lon}`);
  }


  //EBS

  getEbs(imei): Observable<any> {
    return this.apiService.get(`/get-ebses/${imei}`);
  }

  addEbs(imei, data): Observable<any> {
    return this.apiService.post(`/addSensor/${imei}`, data);
  }

  editEbs(data): Observable<any> {
    return this.apiService.post(`/editSensor`, data);
  }

  deleteEbs(sn): Observable<any> {
    return this.apiService.delete(`/delete-ebs/${sn}`);
  }

  checkSn(sn): Observable<any> {
    return this.apiService.get(`/checkSN/${sn}`);
  }

  //communicate network

  getDiseases(imei): Observable<any> {
    return this.apiService.get(`/getDiseases/${imei}`);
  }

  getContacts(imei): Observable<any> {
    return this.apiService.get(`/getContacts/${imei}`);
  }

  editContacts(imei, data): Observable<any> {
    return this.apiService.patch(`/editContacts/${imei}`, data, { responseType: 'text' });
  }

  editContactsHealth(imei, data): Observable<any> {
    return this.apiService.patch(`/editContactsHealth/${imei}`, data);
  }

  deleteContact(id, imei): Observable<any> {
    return this.apiService.delete(`/deleteContactHealth/${id}/${imei}`);
  }

  editDiseases(imei, data): Observable<any> {
    return this.apiService.patch(`/editDiseases/${imei}`, data);
  }

  //CPL
  getNewCPL(id): Observable<any> {
    return this.apiService.get(`/alarm/meas/${id}`);
  }

  //Carehome
  getHours(imei): Observable<any> {
    return this.apiService.get(`/getHours/${imei}`);
  }


  addExtraNoteToAlarm(data): Observable<any> {
    return this.apiService.post('/alarm/addExtraNote', data);
  }

  //documents
  getDocumentFromDocumentation(id): Observable<any> {
    return this.apiService.get(`/getDocumentation/${id}`);
  }


  uploadDocumentToDocumentation(data): Observable<any> {
    return this.apiService.post('/saveDocumentFile', data);
  }

  deleteDocument(id): Observable<any> {
    return this.apiService.delete(`/deleteDocumentFile/${id}`);
  }

  getDocuments(imei, termsNeed: boolean = false): Observable<any> {
    return this.apiService.get(`/getDocuments/${imei}/${termsNeed}`);
  }

  saveTermsAccepted(data): Observable<any> {
    return this.apiService.post('/saveTermsAccepted', data);
  }

  getRegulation(): Observable<any> {
    return this.apiService.get(`/getRegulation`);
  }


  // projects

  getProjectsForManager(): Observable<any> {
    return this.apiService.get(`/getProjects`);
  }

  getProjectStats(): Observable<any> {
    return this.apiService.get(`/project-stats`);
  }

  getProject(id): Observable<any> {
    return this.apiService.get(`/getProject/${id}`);
  }

  checkFreeNameOfProject(): Observable<any> {
    return this.apiService.get(`/checkIfNameProjectIsFree`);
  }

  assignAllPatientsToProject(data): Observable<any> {
    return this.apiService.post('/manager/assign-all-patients-to-project', data);
  }

  deletePatientFromProject(data): Observable<any> {
    return this.apiService.post('/manager/delete-patient-from-project', data);
  }

  addProject(projectName, purchaser, place, contractstart, contractend): Observable<any> {
    return this.apiService.post(`/create-project-health`, { projectName: projectName, purchaser: purchaser, place: place, contractStart: contractstart, contractEnd: contractend });
  }

  editProject(id, projectName, purchaser, place, contractstart, contractend): Observable<any> {
    return this.apiService.post(`/edit-project/${id}`, { id: id, projectName: projectName, purchaser: purchaser, place: place, contractStart: contractstart, contractEnd: contractend });
  }

  deleteProject(id: number): Observable<any> {
    return this.apiService.delete(`/delete-project/${id}`);
  }

  deleteMultipleProjects(ids: number[]): Observable<any> {
    return this.apiService.post(`/delete-projects`, { ids: ids });
  }

  getUsersWithDeactivePanel(id): Observable<any> {
    return this.apiService.get(`/manager/users-deactivated-panel/${id}`);
  }

  setActiveSidePanelForUser(id): Observable<any> {
    return this.apiService.get(`/manager/set-active-side-panel/${id}`);
  }

  checkIfManagerHasProject(): Observable<any> {
    return this.apiService.get(`/checkIfManagerHasProject`);
  }

  getSideNav(): Observable<any> {
    return this.apiService.get(`/getSidenavNotifs`);
  }

  startHandleSideNotif(id): Observable<any> {
    return this.apiService.get(`/startHandle/sideNotif/${id}`);
  }

  handleSideNotif(id, note, turnOffSideNotifs): Observable<any> {
    return this.apiService.get(`/handle/sideNotif/${id}?note=${note}&turnOffSideNotifs=${turnOffSideNotifs}`);
  }

  getCityFromZipCode(zipCode): Observable<any> {
    return this.apiService.get(`/patient/zip-code/${zipCode}`);
  }

  //indivClient

  changeStatus(status): Observable<any> {
    return this.apiService.get(`/patient/changeStatusOfUser/${status}`);
  }

  getBandCard(imei): Observable<any> {
    return this.apiService.get(`/getBandCard/${imei}`);
  }

  getAvatar(userId): Observable<any> {
    return this.apiService.get(`/manager/getAvatar/${userId}`);
  }

  getCallData(imei, pesel): Observable<any> {
    return this.apiService.get(`/manager/getCallData/${imei}/${pesel}`);
  }

  getSpecialClient(imei): Observable<any> {
    return this.apiService.get(`/manager/getSpecialClient/${imei}`);
  }

  setUserDescription(imei, description): Observable<any> {
    return this.apiService.patch(`/manager/setUserDescription/${imei}`, { description: description });
  }

  getBandNote(imei): Observable<any> {
    return this.apiService.get(`/manager/getBandNote/${imei}`);
  }

  setBandNote(imei, note): Observable<any> {
    const timenow = moment().format('YYYY-MM-DD HH:mm:ss');
    return this.apiService.post(`/manager/setBandNote/${imei}`, { note: note, date: timenow });
  }

  /** user-scoped (Etap 16): notatki PACJENTA po user_id - takze bez opaski (band_note.user_id, imei 0). */
  getBandNoteByUser(userId): Observable<any> {
    return this.apiService.get(`/manager/getBandNote/by-user/${userId}`);
  }

  setBandNoteByUser(userId, note): Observable<any> {
    const timenow = moment().format('YYYY-MM-DD HH:mm:ss');
    return this.apiService.post(`/manager/setBandNote/by-user/${userId}`, { note: note, date: timenow });
  }

  /** Usuniecie pojedynczego wpisu notatki (tylko manager - egzekwowane rowniez w backendzie). */
  deleteBandNote(imei, date: string, author: string): Observable<any> {
    return this.apiService.post(`/manager/deleteBandNote/${imei}`, { date: date, author: author });
  }

  getAlarmLocation(alarmId): Observable<any> {
    return this.apiService.get(`/manager/getAlarmLocation/${alarmId}`);
  }

  getLastLocalizations(imei, number): Observable<any> {
    return this.apiService.get(`/manager/getLastLocations/${number}/${imei}`);
  }

  getLastNotifs(imei): Observable<any> {
    return this.apiService.get(`/patient/getLastNotifs/${imei}?limit=0&excludeCritical=true`);
  }

  getTempMac(imei): Observable<any> {
    return this.apiService.get(`/manager/getTempMac/${imei}`);
  }

  setTempMac(imei, data): Observable<any> {
    return this.apiService.post(`/manager/setTempMac/${imei}`, data);
  }

  deleteTempMac(imei): Observable<any> {
    return this.apiService.delete(`/manager/deleteTempMac/${imei}`);
  }

  getActivities(lang): Observable<any> {
    return this.apiService.get(`/patient/getPlannerActivities/${lang}`);
  }

  setTypeOfGroupResponder(userId, checked): Observable<any> {
    return this.apiService.post(`/patient/setType/responder`, { userId: userId, checked: checked });
  }

  getBandTypeByImei(imei): Observable<any> {
    return this.apiService.get(`/band/getBandTypeByImei/${imei}`,)
  }

  sendLocationSMS(imei): Observable<any> {
    return this.apiService.post(`/send-location-sms/${imei}`, {imei: imei});
  }

  sendCustomSMS(imei, type): Observable<any> {
    return this.apiService.post(`/send-custom-sms/${imei}`, {imei: imei, type: type});
  }

  sendSMS(imei: string, type: string, authenticationToken: string): Observable<any> {
    return this.apiService.post(`/sendSMS`, {
      imei: imei,
      type: type,
      authenticationToken: authenticationToken
    });
  }

  getStatsOfUsers(): Observable<any> {
    return this.apiService.get(`/manager/getStatsOfUsers`);
  }

  getStatsOfUsersByCaregiver(caregiver_id): Observable<any> {
    return this.apiService.get(`/manager/getStatsOfUsersByCaregiver/${caregiver_id}`);
  }

  getStatsOfCaregivers(): Observable<any> {
    return this.apiService.get(`/manager/getStatsOfCaregivers`);
  }

  // Water Intake
  addWaterIntake(imei: string, amount: number, timestamp?: number): Observable<any> {
    const data = {
      amount: amount,
      timestamp: timestamp || Date.now()
    };
    return this.apiService.post(`/patient/${imei}/water-intake`, data);
  }

  deleteWaterIntake(imei: string, id: number): Observable<any> {
    return this.apiService.delete(`/patient/${imei}/water-intake/${id}`);
  }

  getWaterIntake(imei: string, fromDate: string, toDate: string, timeZone: string): Observable<any> {
    return this.apiService.get(`/patient/${imei}/water-intake?${instantWindowQuery(fromDate, toDate)}&timezone=${encodeURIComponent(timeZone)}`);
  }

  getPressureData(imei: string, fromDate: string, toDate: string, timeZone: string): Observable<any> {
    return this.apiService.get(`/patient/${imei}/pressure?${instantWindowQuery(fromDate, toDate)}&timezone=${encodeURIComponent(timeZone)}`);
  }

  // POST /band/addBloodPressure — imei goes in the body (the /band class resolves the Band by imei).
  // timestamp is optional (seconds or ms); when omitted the backend falls back to "now".
  addBloodPressure(imei: string, systolic: number, diastolic: number, pulse: number, timestamp?: number): Observable<any> {
    const data: any = {
      imei: imei,
      systolic: systolic,
      diastolic: diastolic,
      pulse: pulse
    };
    if (timestamp) {
      data.timestamp = timestamp;
    }
    return this.apiService.post('/band/addBloodPressure', data);
  }

  getSleepData(imei: string, fromDate: string, toDate: string): Observable<any> {
    return this.apiService.get(`/patient/${imei}/sleep?fromDate=${fromDate}&toDate=${toDate}`);
  }

  // Band Settings (fall detector + volume)
  getBandSettings(imei: string): Observable<any> {
    return this.apiService.get(`/patient/${imei}/band-settings`);
  }

  // Fall Detector
  updateFallDetectorLevel(imei: string, fallDetectorLevel: number, alarmId: number): Observable<any> {
    const data = {
      fallDetectorLevel: fallDetectorLevel,
      alarmId: alarmId
    };
    return this.apiService.post(`/patient/${imei}/fall-detector-level`, data);
  }

  // Volume Level
  updateVolumeLevel(imei: string, volumeLevel: number): Observable<any> {
    const data = {
      volumeLevel: volumeLevel
    };
    return this.apiService.post(`/patient/${imei}/volume-level`, data);
  }

  getAlarmPreview(imei: string, date: string, type: string): Observable<any> {
    return this.apiService.get(`/alarm/preview/${imei}?date=${encodeURIComponent(date)}&type=${type}`);
  }


      // Call Logs API methods - zaktualizowane do nowych endpointów
  getCallLogs(page: number = null, limit: number = null, filters: any = null): Observable<any> {
    let params = '';
    const queryParams = [];

    if (page) queryParams.push(`page=${page}`);
    if (limit) queryParams.push(`limit=${limit}`);
    // Usunięto user_id i external_number - backend automatycznie pobiera numery na podstawie parent_id
    if (filters && filters.date_from) queryParams.push(`date_from=${filters.date_from}`);
    if (filters && filters.date_to) queryParams.push(`date_to=${filters.date_to}`);
    if (filters && filters.call_successful !== '' && filters.call_successful !== null) {
      queryParams.push(`status=${filters.call_successful}`);
    }

    if (queryParams.length > 0) {
      params = '?' + queryParams.join('&');
    }

    return this.apiService.get(`/ai/call-logs${params}`);
  }

  getCallLog(conversationId: string): Observable<any> {
    return this.apiService.get(`/ai/conversation/${conversationId}`);
  }

  deleteCallLog(id: number): Observable<any> {
    // Reach call logi siedzą w reach_call_log; stary /ai/call-logs/{id} nie istniał (404 → fix #83).
    return this.apiService.delete(`/manager/reach/call-logs/${id}`);
  }

  // Eksport call logów do CSV - backend generuje plik z nagłówkami w języku user.lang
  exportCallLogsCsv(filters: any = {}): Observable<Blob> {
    const queryParams = [];
    if (filters.date_from) queryParams.push(`date_from=${filters.date_from}`);
    if (filters.date_to) queryParams.push(`date_to=${filters.date_to}`);
    if (filters.call_successful) queryParams.push(`status=${filters.call_successful}`);
    const params = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    return this.http.get(`${environment.api_url}/ai/call-logs/export${params}`, {
      responseType: 'blob'
    });
  }

  // Karta pacjenta jako PDF — przycisk „Pobierz kartę” w trybie telecentrum (SDLY-3031),
  // ten sam endpoint co na SiDLY-Telecare. Backend oddaje kartę tylko pacjentom własnej
  // organizacji; na obcy IMEI odpowiada 404.
  downloadPatientCard(imei): Observable<Blob> {
    return this.http.get(`${environment.api_url}/patientCard/${imei}/import/pdf`, {
      responseType: 'blob'
    });
  }

  // Pobranie ZIP-a z pełnym podsumowaniem użytkownika (PDF) + pomiarami (Excel).
  // Nagłówek X-Access-Token dokłada globalny HttpTokenInterceptor.
  downloadUserSummary(imei): Observable<Blob> {
    return this.http.get(`${environment.api_url}/other/userSummary/${imei}`, {
      responseType: 'blob'
    });
  }

  // Masowy import pacjentów z Excela (tylko ROLE_API_MANAGER).
  // multipart: pole `file` (.xlsx/.xls) + opcjonalny `lang` (język maila powitalnego).
  // Zwraca podsumowanie per wiersz: { created, failed, rows: [{row, imei, status, errors}] }.
  // Nagłówek X-Access-Token dokłada globalny HttpTokenInterceptor.
  importPatientsExcel(file: File, lang?: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (lang) {
      formData.append('lang', lang);
    }
    return this.http.post(`${environment.api_url}/manager/patient/import-excel`, formData);
  }

  // Gotowy szablon XLSX (nagłówki + przykładowy wiersz) do importu.
  // `lang` to kod języka UI zapisany WIELKIMI literami (PL, EN, DE…) — w takiej postaci
  // rozpoznaje go generator backendu. Wcześniejsze założenie, że znosi dowolny zapis,
  // okazało się nieprawdziwe: przy „pl” oddawał plik po angielsku. Kodu spoza swojej
  // listy backend nie odrzuca, tylko sam schodzi na angielski.
  // Oddajemy całą odpowiedź, bo nazwa pliku (z sufiksem języka) siedzi w Content-Disposition.
  downloadImportTemplate(lang?: string): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    if (lang) {
      params = params.set('lang', lang);
    }
    return this.http.get(`${environment.api_url}/manager/patient/import-excel/template`, {
      params,
      responseType: 'blob',
      observe: 'response'
    });
  }

  // Metoda do wykonywania masowych połączeń przez backend proxy
  makeCallsBulk(payload: any): Observable<any> {
    return this.apiService.post('/ai/make_calls_bulk', payload);
  }

  // Pobierz wszystkie opaski dla zalogowanego managera (dla AI Paramedic)
  getAllBands(): Observable<any> {
    return this.apiService.get('/manager/getAllBands');
  }

  getScanInterval(): Observable<any> {
    return this.apiService.get('/manager/getScanInterval');
  }

  setScanInterval(scanInterval: number): Observable<any> {
    return this.apiService.postV2(`/manager/setScanInterval/${scanInterval}`);
  }

  // Pobierz ostatnie alarmy (wszystkie typy) dla danego IMEI
  getRecentAlarms(imei: string, limit: number = 20, includeHandled: boolean = true): Observable<any> {
    const params = `?limit=${limit}&includeHandled=${includeHandled}`;
    return this.apiService.get(`/patient/${imei}/alarms/recent${params}`);
  }

  // Pobierz ryzyko upadku z Sidly AI
  getFallRisk(imei: string): Observable<any> {
    return this.apiService.get(`/fallprediction/${imei}`);
  }
}
